import {
  LayoutDashboard, Shield, Brain, Route, BookOpen, Bot, Sparkles,
  DollarSign, Users, Settings, Layout, Package, AppWindow, ShieldCheck,
  BarChart3, AlertCircle, Clock, Database, Target, BarChart2, Building2,
  Cpu, Aperture, KeyRound, Globe, GitMerge, Network, Drama, Rocket, TestTube,
  ShieldAlert, Plug, Activity, Fingerprint, ClipboardList, Coins, GitBranch, ScrollText, Scale,
  Workflow, Library, IdCard, ShieldHalf,
} from 'lucide-react';

export const C = {
  navy: "#0A1628", dark: "#0D1F35",
  card: "#161b30",
  deep: "#0f1629",
  border: "#272749",
  cardBorder: "#334155",
  surface: "#0C1E38",
  orange: "#ff5b1f", teal: "#00C9A7", green: "#39B54A", cyan: "#00D4FF",
  white: "#FFFFFF",
  muted: "#b3b3b3",
  inp: "#0f172a80",
  sub: "#94A3B8FF",
  dim: "#475569", red: "#FF5050", amber: "#FFBC42", purple: "#7B5FFF", pink: "#EC4899",
};

export const NAV = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  {
    // Connectors come first: guardrail providers, IdPs, Vault, Redis and
    // storage must be connected before Gateway pages can use them.
    id: "configuration",
    label: "Configuration",
    items: [
      { id: "connectors", label: "Connectors", icon: Plug },
    ],
  },
  {
    id: "onboarding",
    label: "Onboarding Hierarchy",
    icon: Building2,
    items: [
      { id: "organizations", label: "Hierarchy", icon: Globe },
      { id: "access-center", label: "Access Center", icon: KeyRound },
    ],
  },
  {
    id: "ai-proxy-development",
    label: "Gateway",
    items: [
      // Enrichment/Security/Model Abstraction profiles are configured first —
      // LLM Models and Routes then attach them.
      { id: "enrichment", label: "Enrichment", icon: Sparkles },
      { id: "security-hub", label: "Security", icon: ShieldAlert },
      { id: "model-abstraction", label: "Model Abstraction", icon: Drama },
      { id: "models", label: "LLM Model", icon: Brain },
      { id: "routes", label: "LLM Routes", icon: Route },
      { id: "ai-proxy", label: "AI Gateway", icon: Layout },
      { id: "mcp-proxy", label: "MCP Gateway", icon: Network },
      { id: "agent-proxy", label: "Agent Gateway", icon: Bot },
    ],
  },
  {
    id: "distribution",
    label: "Distribution",
    items: [
      { id: "product", label: "Product", icon: Package },
      { id: "consumer", label: "Consumer", icon: AppWindow },
      { id: "developer", label: "Developer", icon: Users },
    ],
  },
  {
    id: "management-section",
    label: "Management",
    items: [
      { id: "environments", label: "Environments", icon: Cpu },
      { id: "gateway-env", label: "Gateway", icon: Aperture },
      { id: "cicd-automation", label: "CI/CD Automation", icon: GitMerge },
    ],
  },
  {
    id: "governance-section",
    label: "Governance",
    items: [
      { id: "governance", label: "Governance", icon: ShieldCheck },
      { id: "agent-governance", label: "Agent Governance", icon: Fingerprint },
      { id: "ai-governance", label: "AI Governance", icon: Scale },
      { id: "exception-management", label: "Exception Management", icon: ClipboardList },
    ],
  },
  { id: "security", label: "Security Center", icon: Shield },
  {
    id: "observability",
    label: "Observability",
    items: [
      { id: "monitoring", label: "Monitoring", icon: Activity },
      { id: "trace", label: "Trace", icon: GitBranch },
      { id: "enterprise-audit", label: "Enterprise Audit Trail", icon: ScrollText },
      { id: "cost-control", label: "Cost Control", icon: Coins },
      { id: "metrics", label: "AI Proxy Metrics", icon: BarChart3 },
      { id: "error-codes", label: "Error Code Analysis", icon: AlertCircle },
      { id: "latency", label: "Latency Analysis", icon: Clock },
      { id: "cache", label: "Cache Performance", icon: Database },
      { id: "target", label: "Target Performance", icon: Target },
      { id: "report", label: "Report", icon: BarChart2 },
    ],
  },
  {
    id: "deployment",
    label: "Deployment",
    items: [
      { id: "api-deploy", label: "Deployment", icon: Rocket },
      { id: "api-test", label: "Testing", icon: TestTube },
    ],
  },
  {
    id: "agent-ai",
    label: "Agentic AI",
    items: [
      { id: "agent-harness", label: "Agent Harness", icon: Workflow },
      { id: "agent-inventory", label: "Agent Inventory", icon: Library },
      { id: "agent-identity", label: "Agent Identity", icon: IdCard },
      { id: "agent-rbac", label: "Agent RBAC", icon: ShieldHalf },
      { id: "rag", label: "RAG", icon: BookOpen },
      { id: "agents", label: "Agent Connect", icon: Bot },
    ],
  },
  {
    id: "monetize-section",
    label: "Monetize",
    items: [
      { id: "monetize", label: "Monetize", icon: DollarSign },
      { id: "developers", label: "Developers", icon: Users },
      { id: "settings", label: "Settings", icon: Settings },
      { id: "general-rbac", label: "General RBAC", icon: KeyRound },
    ],
  },
];

export const sevC = {
  critical: C.red,
  high: C.orange,
  medium: C.amber,
  low: C.teal
};

export const cbC = {
  closed: C.green,
  open: C.red,
  "half-open": C.amber
};

export const CONFIG_OPTIONS = [
  {v: 'git', l: 'Git Configuration'},
  {v: 'gcs', l: 'Google Cloud Storage'},
  {v: 'standard', l: 'AI Proxy Standard Linting'},
  {v: 'custom', l: 'AI Proxy Custom Linting'}
];

// Apigee org used for API Products / Apps / Developers calls against
// fsp-apigee-wrapper-ai-svc. Falls back to a placeholder so unset envs fail
// obviously (as an Apigee 404/permission error) rather than silently.
export const APIGEE_ORG = process.env.REACT_APP_APIGEE_ORG || 'your-gateway-org';

// Organization id used for Probestack Onboarding API calls (see
// http-service/onboardingClient.js). Hardcoded for now, same as the backend.
export const ONBOARDING_ORG_ID = process.env.REACT_APP_ONBOARDING_ORG_ID || 'f52c02e6-d67a-4bc9-8e94-36e9d4b8d30c';


